import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import DashboardLayout from '../../../examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '../../../examples/Navbars/DashboardNavbar';
import type { RefundItem } from './components/RefundsTab';
import { apiCall } from '../../../utils/apiClient';

type LocationState = {
  refund?: RefundItem;
  bookingId?: number | string;
};

type DetailRow = {
  label: string;
  value: React.ReactNode;
};

const formatText = (value: unknown): string => {
  if (value === null || value === undefined) return '-';
  const text = String(value).trim();
  return text.length ? text : '-';
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const formatCurrency = (value: number | string | null | undefined): string => {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) return 'Rs 0.00';
  return `Rs ${amount.toFixed(2)}`;
};

const toTitleCase = (value: string): string =>
  value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());

const STATUS_OPTIONS = ['pending', 'approved', 'rejected', 'processed'] as const;
const METHOD_OPTIONS = ['manual', 'gateway', 'upi', 'bank_transfer'] as const;

const normalizeMethod = (value: string): string => {
  if (!value || value === '-') return 'manual';
  return value.toLowerCase().replace(/\s+/g, '_');
};

const normalizeStatus = (value: string): string => {
  const normalized = String(value || 'pending').toLowerCase();
  if (normalized === 'accepted') return 'approved';
  return STATUS_OPTIONS.includes(normalized as (typeof STATUS_OPTIONS)[number]) ? normalized : 'pending';
};

const getStatusClass = (status: string): string => {
  const normalized = status.toLowerCase();
  if (normalized === 'approved' || normalized === 'processed') {
    return 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200';
  }
  if (normalized === 'rejected' || normalized === 'failed') {
    return 'bg-rose-100 text-rose-700 ring-1 ring-rose-200';
  }
  return 'bg-amber-100 text-amber-700 ring-1 ring-amber-200';
};

const DetailsCard: React.FC<{ title: string; rows: DetailRow[]; className?: string }> = ({
  title,
  rows,
  className = '',
}) => (
  <div className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ring-1 ring-slate-100 ${className}`}>
    <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{title}</h3>
    <div className="space-y-2">
      {rows.map((row) => (
        <div
          key={row.label}
          className="grid grid-cols-1 items-start gap-1 border-b border-slate-100 pb-2 last:border-b-0 last:pb-0 sm:grid-cols-[minmax(120px,1fr)_minmax(140px,1.2fr)] sm:gap-2"
        >
          <span className="text-xs text-slate-500">{row.label}</span>
          <span className="break-words text-sm font-semibold text-slate-800 sm:text-right">{row.value}</span>
        </div>
      ))}
    </div>
  </div>
);

const RefundDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const sourceRefund = state?.refund;
  const [refund, setRefund] = useState<RefundItem | null>(sourceRefund ?? null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    status: normalizeStatus(sourceRefund?.status || 'pending'),
    refund_method: normalizeMethod(sourceRefund?.refundMethod || 'manual'),
    gateway_refund_id: sourceRefund?.gatewayRefundId && sourceRefund.gatewayRefundId !== '-' ? sourceRefund.gatewayRefundId : '',
    notes: sourceRefund?.notes || '',
  });
  const resolvedBookingId =
    refund?.bookingId ??
    (state?.bookingId !== null && state?.bookingId !== undefined
      ? String(state.bookingId)
      : null);

  useEffect(() => {
    if (!sourceRefund) return;
    setRefund(sourceRefund);
    setForm({
      status: normalizeStatus(sourceRefund.status || 'pending'),
      refund_method: normalizeMethod(sourceRefund.refundMethod || 'manual'),
      gateway_refund_id:
        sourceRefund.gatewayRefundId && sourceRefund.gatewayRefundId !== '-'
          ? sourceRefund.gatewayRefundId
          : '',
      notes: sourceRefund.notes || '',
    });
  }, [sourceRefund]);

  if (!refund) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div className="mt-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-800">Refund Details</h2>
          <p className="mt-2 text-sm text-gray-600">Refund data is not available. Open this page from booking refund details.</p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-4 rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Go Back
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const handleGoToBooking = () => {
    if (!resolvedBookingId || resolvedBookingId === '-') {
      toast.error('Booking ID is not available for this refund');
      return;
    }

    const numericBookingId = Number(resolvedBookingId);
    navigate('/booking-details', {
      state: {
        booking_id: Number.isNaN(numericBookingId) ? resolvedBookingId : numericBookingId,
      },
    });
  };

  const overviewRows: DetailRow[] = [
    { label: 'Refund ID', value: formatText(refund.refundId) },
    {
      label: 'Booking Code',
      value: (
        <button
          type="button"
          onClick={handleGoToBooking}
          className="text-sky-700 underline underline-offset-2 hover:text-sky-800"
        >
          {formatText(refund.bookingCode)}
        </button>
      ),
    },
    { label: 'Service', value: formatText(refund.displayServiceName) },
    { label: 'Refund Scope', value: formatText(refund.refundScope) },
    { label: 'Refund Type', value: formatText(refund.refundType) },
    { label: 'Status', value: formatText(refund.status) },
    { label: 'Reason', value: formatText(refund.reason) },
    { label: 'Notes', value: formatText(refund.notes) },
  ];

  const amountRows: DetailRow[] = [
    { label: 'Base Amount', value: formatCurrency(refund.baseAmount) },
    { label: 'Discount Amount', value: formatCurrency(refund.discountAmount) },
    { label: 'Taxable Amount', value: formatCurrency(refund.taxableAmount) },
    { label: 'CGST Rate', value: `${refund.cgstRate.toFixed(2)}%` },
    { label: 'SGST Rate', value: `${refund.sgstRate.toFixed(2)}%` },
    { label: 'CGST Amount', value: formatCurrency(refund.cgstAmount) },
    { label: 'SGST Amount', value: formatCurrency(refund.sgstAmount) },
    { label: 'Total Refund Amount', value: formatCurrency(refund.totalRefundAmount) },
  ];

  const flowRows: DetailRow[] = [
    { label: 'Requested By Type', value: formatText(refund.requestedByType) },
    { label: 'Requested By ID', value: formatText(refund.requestedById) },
    { label: 'Processed By Type', value: formatText(refund.processedByType) },
    { label: 'Processed By ID', value: formatText(refund.processedById) },
    { label: 'Processed At', value: formatDateTime(refund.processedAt) },
    { label: 'Refund Method', value: formatText(refund.refundMethod) },
    { label: 'Gateway Refund ID', value: formatText(refund.gatewayRefundId) },
  ];

  const statusLabel = formatText(refund.status);
  const isProcessedSelection = form.status === 'processed';

  const handleUpdateStatus = async () => {
    if (!refund) return;

    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        status: form.status,
      };
      if (form.status === 'processed') {
        payload.refund_method = form.refund_method;
        payload.gateway_refund_id = form.gateway_refund_id?.trim() || null;
        payload.notes = form.notes?.trim() || null;
      }

      const res = await apiCall({
        endpoint: `admin/booking/refunds/${refund.id}/status`,
        method: 'PUT',
        data: payload,
      });

      if (res && (res.status === 200 || res.status === 201 || res.success === true)) {
        toast.success('Refund status updated successfully');
        setRefund((prev) =>
          prev
            ? {
                ...prev,
                status: form.status,
                refundMethod: form.status === 'processed' ? form.refund_method : prev.refundMethod,
                gatewayRefundId:
                  form.status === 'processed' ? form.gateway_refund_id?.trim() || '-' : prev.gatewayRefundId,
                notes: form.status === 'processed' ? form.notes?.trim() || '' : prev.notes,
                updatedAt: new Date().toISOString(),
              }
            : prev
        );
        return;
      }

      toast.error(res?.message || 'Failed to update refund status');
    } catch (error) {
      console.error('Error updating refund status:', error);
      toast.error('Failed to update refund status');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div className="mt-5 space-y-4">
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-sky-50 to-cyan-50 p-5 shadow-md ring-1 ring-slate-100">
          <div className="absolute right-0 top-0 h-24 w-24 -translate-y-6 translate-x-8 rounded-full bg-sky-200/40 blur-2xl" />
          <div className="absolute left-0 bottom-0 h-20 w-20 -translate-x-8 translate-y-6 rounded-full bg-cyan-200/30 blur-2xl" />
          <div className="relative flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">Refund Details</p>
              <h2 className="mt-1 text-2xl font-bold text-slate-900">Refund #{formatText(refund.refundId)}</h2>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className={`inline-flex rounded-full px-3 py-1.5 text-sm font-semibold ${getStatusClass(statusLabel)}`}>
                  {statusLabel}
                </span>
                <button
                  type="button"
                  onClick={handleGoToBooking}
                  className="inline-flex rounded-full bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-600 ring-1 ring-slate-200 transition-colors hover:bg-slate-200"
                >
                  Booking {formatText(refund.bookingCode)}
                </button>
              </div>
              <p className="mt-2 text-sm text-slate-600">Complete refund information for this booking/service.</p>
              <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-xl border border-sky-100 bg-white/90 px-3 py-2 shadow-sm">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Total Refund</p>
                  <p className="mt-1 text-lg font-bold text-sky-700">{formatCurrency(refund.totalRefundAmount)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white/90 px-3 py-2 shadow-sm">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Refund Type</p>
                  <p className="mt-1 text-base font-semibold text-slate-900">{formatText(refund.refundType)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white/90 px-3 py-2 shadow-sm">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Refund Scope</p>
                  <p className="mt-1 text-base font-semibold text-slate-900">{formatText(refund.refundScope)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white/90 px-3 py-2 shadow-sm">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">Last Updated</p>
                  <p className="mt-1 text-sm font-semibold text-slate-900">{formatDateTime(refund.updatedAt)}</p>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                Back
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-gradient-to-r from-emerald-50 via-white to-cyan-50 p-4 shadow-sm ring-1 ring-emerald-100">
          <div className="mb-3 flex items-start justify-between gap-2">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-700">Update Status</h3>
            </div>
            <span className={`inline-flex rounded-full px-3 py-1.5 text-sm font-semibold ${getStatusClass(form.status)}`}>
              {toTitleCase(form.status)}
            </span>
          </div>

          <div
            className={`grid grid-cols-1 gap-3 ${
              isProcessedSelection ? 'lg:grid-cols-[170px_180px_1fr_1fr_auto]' : 'lg:grid-cols-[220px_auto]'
            }`}
          >
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none ring-sky-200 transition focus:ring-2"
              >
                <option value="pending">Pending</option>
                <option value="approved">Accepted</option>
                <option value="processed">Processed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {isProcessedSelection && (
              <>
                <div>
                  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Refund Method</label>
                  <select
                    value={form.refund_method}
                    onChange={(e) => setForm((prev) => ({ ...prev, refund_method: e.target.value }))}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none ring-sky-200 transition focus:ring-2"
                  >
                    {METHOD_OPTIONS.map((method) => (
                      <option key={method} value={method}>
                        {toTitleCase(method)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Gateway Refund ID</label>
                  <input
                    type="text"
                    value={form.gateway_refund_id}
                    onChange={(e) => setForm((prev) => ({ ...prev, gateway_refund_id: e.target.value }))}
                    placeholder="Optional"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none ring-sky-200 transition focus:ring-2"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Notes</label>
                  <input
                    type="text"
                    value={form.notes}
                    onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                    placeholder="Optional notes"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none ring-sky-200 transition focus:ring-2"
                  />
                </div>
              </>
            )}
            <div className="flex items-end">
              <button
                type="button"
                onClick={handleUpdateStatus}
                disabled={submitting}
                className="w-full rounded-lg bg-slate-900 px-5 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 lg:w-auto"
              >
                {submitting ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <DetailsCard title="Overview" rows={overviewRows} />
          <DetailsCard title="Amount Breakdown" rows={amountRows} />
          <DetailsCard title="Processing Details" rows={flowRows} />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RefundDetailsPage;
